import { CloseIcon, SearchIcon, ViewIcon, DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    DrawerBody,
    DrawerHeader,
    Flex,
    HStack,
    Heading,
    IconButton,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    VStack,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import AppDatePicker from "components/AppDatePicker";
import AppSelect from "components/AppSelect";
import AppViewDrawer from "components/AppViewDrawer";
import AppViewListItem from "components/AppViewListItem";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useUserRole } from "hooks/useUserRole";
import { useCache } from "hooks/useCache";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Store } from "store";
import { showToast, toastTypes } from "utils";
import { TAX_SUPER_ADMIN } from "utils/roles";
import { ReactTable } from "../../components/ReactTable";

// "reference": payment_object.reference,
// "status": payment_object.status,
// "id": payment_object.id,
// "tax_assessment_id": payment_object.tax_assessment_id,
// "method": payment_object.method,
// "provider": payment_object.provider,
// "reason": payment_object.reason,
// "receipt": payment_object.receipt,
// "amount": payment_object.amount,

type Table3RowType = {
  id: number;
  reference: string;
  tax_payer_name: string;
  tax_payer_email: string;
  tax_payer_phone: string;
  created_at: string;
  name: string;
  status: string;
  method: string;
  provider: string;
  amount: string;
  paid_by: string;
};

export default function TaxPaymentManagementSection() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();
  const { dispatch } = useContext(Store);
  const { hasPermission } = useAuthentication();
  const userRole = useUserRole();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [institution, setInstitution] = useState<string>("");
  const [taxNature, setTaxNature] = useState<string>("");
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const { getTransactions, getInstitutions, getTaxNatures, deleteTaxPayment } = useApi();
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(),
    new Date(),
  ]);
  const {
    data: institutions,
    isLoading: isInstitutionLoading,
    mutate: mutateInstitution,
  } = useCache("institutions", () => getInstitutions(1, institution, ""));
  const { data: taxNatures, isLoading: isTaxNatureLoading } = useCache(
    "taxNatures",
    () => getTaxNatures(1, "", institution, "")
  );
  const { data: transactions, mutate } = useCache("transactions", () =>
    getTransactions(
      page,
      searchBarValue34,
      pageSize,
      selectedDates[0]
        ?.toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-"),
      selectedDates[1]
        ?.toISOString()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("-"),
      institution,
      taxNature
    )
  );
  const handleSearch = (event: any) => {
    const value = event.target.value;
    mutate(getTransactions(page, value));
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getTransactions(page, ""));
  };

  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getTransactions(pg));
  };
  const handleSetNextPage = () => {
    if (transactions.length > 0 && transactions[0].num_of_pages >= page + 1) {
      setPage((prev: any) => prev + 1);
      mutate(getTransactions(page + 1));
    }
  };
  const handleSetPrevPage = () => {
    if (transactions.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(getTransactions(page + 1));
    }
  };

  const handleViewItem = useCallback(
    (item: any) => {
      setActiveItem(item);
      onOpen();
    },
    [onOpen, setActiveItem]
  );

  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("tax_payer_name", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Nom</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("tax_payer_email", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Email</Heading>
          </Flex>
        ),
        meta: { width: "132px" },
      }),
      table3ColumnHelper.accessor("amount", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Montant</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("created_at", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Date</Heading>
          </Flex>
        ),
        meta: { width: "132px" },
      }),
      table3ColumnHelper.accessor("paid_by", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Payé par</Heading>
          </Flex>
        ),
        meta: { width: "132px" },
      }),
      table3ColumnHelper.accessor("status", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>
              {info.getValue<string>() === "paid" ? "payé" : "pas à jour"}
            </Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Statut</Heading>
          </Flex>
        ),
        meta: { width: "90px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"} width={"100%"} justifyContent={"flex-end"}>
            {hasPermission("taxpayment", "get") && (
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="view"
                size={"sm"}
                icon={<ViewIcon />}
                borderRadius={"4px"}
                onClick={() => handleViewItem(info.row.original)}
              />
            )}
            {hasPermission("taxpayment", "delete") && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
          </HStack>
        ),
        header: (info) => (
          <Flex width={"100%"} justifyContent={"flex-end"} px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "70px" },
      }),
    ];
  }, [handleViewItem, hasPermission, isDeleting, activeItem, onConfirmOpen]);

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
        minHeight={"100vh"}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
              Transactions
            </Heading>
            <Flex
              pl="56px"
              pr="130px"
              gap="13px"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf={{ base: "stretch", sm: "auto" }}
              flexDirection={{ base: "column", sm: "row" }}
              px={{ md: 0, base: "20px" }}
            ></Flex>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              flex={1}
              alignItems="center"
              alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
            >
              <AppDatePicker
                selectedDates={selectedDates}
                onDateChange={(date) => {
                  setSelectedDates(date);
                  mutate(
                    getTransactions(
                      page,
                      searchBarValue34,
                      pageSize,
                      selectedDates[0]
                        ?.toISOString()
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-"),
                      selectedDates[1]
                        ?.toISOString()
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-"),
                      institution,
                      taxNature
                    )
                  );
                }}
              />
              <Flex width={"60%"} direction={"row"} gap={2}>
                {!isInstitutionLoading &&
                  userRole === TAX_SUPER_ADMIN && (
                    <AppSelect
                      height={"40px"}
                      id="institution"
                      name="institution"
                      onChange={(field: string, value: any) => {
                        setInstitution(value);
                        mutateInstitution(
                          getInstitutions(1, +(institution || 0), value)
                        );
                        mutate(
                          getTransactions(
                            page,
                            searchBarValue34,
                            pageSize,
                            selectedDates[0]
                              ?.toISOString()
                              .split("T")[0]
                              .split("-")
                              .reverse()
                              .join("-"),
                            selectedDates[1]
                              ?.toISOString()
                              .split("T")[0]
                              .split("-")
                              .reverse()
                              .join("-"),
                            value,
                            taxNature
                          )
                        );
                      }}
                      value={institution as string}
                      placeholder="Veuillez sélectionner une institution"
                      options={institutions}
                      isMulti={false}
                      isLoading={isInstitutionLoading}
                    />
                  )}
                {!isTaxNatureLoading && (
                  <AppSelect
                    height={"40px"}
                    id="taxNature"
                    name="taxNature"
                    onChange={(field: string, value: any) => {
                      setTaxNature(value);
                      mutate(
                        getTransactions(
                          page,
                          searchBarValue34,
                          pageSize,
                          selectedDates[0]
                            ?.toISOString()
                            .split("T")[0]
                            .split("-")
                            .reverse()
                            .join("-"),
                          selectedDates[1]
                            ?.toISOString()
                            .split("T")[0]
                            .split("-")
                            .reverse()
                            .join("-"),
                          institution,
                          value
                        )
                      );
                    }}
                    value={taxNature as string}
                    placeholder="Veuillez sélectionner nature de règlement"
                    options={taxNatures}
                    isMulti={false}
                    isLoading={isTaxNatureLoading}
                  />
                )}
              </Flex>
              <InputGroup w={{ base: "100%", sm: "30%" }}>
                <Input
                  placeholder={`Rechercher un Débiteur`}
                  value={searchBarValue34}
                  onChange={(event: any) =>
                    setSearchBarValue34(event.currentTarget.value)
                  }
                  gap="8px"
                  borderWidth="1px"
                  borderRadius="8px"
                />
                <InputRightElement>
                  {searchBarValue34?.length > 0 ? (
                    <CloseIcon onClick={handleClearSearch} />
                  ) : null}
                </InputRightElement>
              </InputGroup>
              <IconButton
                onClick={handleSearch}
                ml={2}
                borderRadius={"10px"}
                py={"19px"}
                border={"1px solid #e2e4e6"}
                aria-label="search"
                icon={<SearchIcon />}
                justifyContent={"center"}
                alignItems={"center"}
              />
            </Flex>
            <Image
              src="images/img_dot_menu.svg"
              alt="Menu Image"
              h="24px"
              w={{ md: "24px", base: "100%" }}
            />
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={transactions}
          />
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={
              transactions &&
              transactions.length > 0 &&
              transactions[0].num_of_pages
            }
          />
        </Box>
      </Flex>
      <AppViewDrawer isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <DrawerHeader>Fiche Débiteur</DrawerHeader>

        <DrawerBody>
          <VStack alignItems={"flex-start"} gap={"15px"}>
            {hasPermission("taxpayment", "delete") && (
              <Button
                size="xl"
                variant="outline"
                colorScheme="red_origin"
                leftIcon={<DeleteIcon />}
                fontWeight={700}
                gap="8px"
                borderRadius="8px"
                onClick={() => {
                  onConfirmOpen();
                }}
                isDisabled={isDeleting}
                isLoading={isDeleting}
              >
                Supprimer
              </Button>
            )}
            <AppViewListItem label="Reference" value={activeItem?.reference} />
            <AppViewListItem label="Montant" value={activeItem?.amount} />
            <AppViewListItem label="Nom" value={activeItem?.tax_payer_name} />
            <AppViewListItem
              label="Nro de telephone"
              value={activeItem?.tax_payer_phone}
            />
            <AppViewListItem
              label="Email"
              value={activeItem?.tax_payer_email}
            />
            <AppViewListItem
              label="Fournisseur de paiement"
              value={activeItem?.provider}
            />
            <AppViewListItem
              label="Mode de paiement"
              value={`${activeItem?.method}`}
            />
          </VStack>
        </DrawerBody>
      </AppViewDrawer>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="xl" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="xl">
                  Voulez-vous vraiment supprimer le paiement « {activeItem?.reference} » ?
                </Text>
                <Text fontSize="xl">
                  Cette action est irréversible.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" leftIcon={<CloseIcon boxSize={3} />} borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteTaxPayment(activeItem.id);
                  dispatch(
                    showToast({
                      toast,
                      message: "Paiement supprimé avec succès",
                      status: toastTypes.success,
                    })
                  );
                  onConfirmClose();
                  onClose();
                  mutate(getTransactions(
                    page,
                    searchBarValue34,
                    pageSize,
                    selectedDates[0]
                      ?.toISOString()
                      .split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-"),
                    selectedDates[1]
                      ?.toISOString()
                      .split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-"),
                    institution,
                    taxNature
                  ));
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
